// @docs:
//   - docs/core/architecture/SHIPS_PAPERS.md
// Ship's Papers — my PIN for the vessel's Scouts. A Scout shows the vessel-wide papers by
// default; entering your PIN on it shows what you personally are entitled to (incl. your own papers).
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DevicePhoneMobileIcon } from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { useToastStore } from '../../store/toastStore';
import { btnPrimary, btnGhost, input, errMsg } from './shared';

/**
 * New PINs are six digits (the API refuses anything else; PINs set earlier keep
 * working on the Scouts). Written as a character class: the old JSX string
 * attribute "\\d{4,6}" reached the browser with a literal backslash, so every
 * PIN failed the pattern and the form never submitted.
 */
export const SCOUT_PIN_PATTERN = '[0-9]{6}';
export const SCOUT_PIN_LENGTH = 6;

export function ScoutPinCard({ vid }: { vid: string }) {
  const qc = useQueryClient(); const addToast = useToastStore((s) => s.addToast);
  const { data } = useQuery({ queryKey: ['papers-pin', vid], queryFn: () => api.papersMyPin(vid) });
  const [editing, setEditing] = useState(false); const [pin, setPin] = useState(''); const [again, setAgain] = useState(''); const [error, setError] = useState<string | null>(null);
  const save = useMutation({ mutationFn: () => api.papersSetMyPin(vid, pin), onSuccess: () => { qc.invalidateQueries({ queryKey: ['papers-pin', vid] }); setEditing(false); setPin(''); setAgain(''); addToast({ type: 'success', title: 'Scout PIN set' }); }, onError: (e) => setError(errMsg(e, 'Could not set the PIN.')) });
  const clear = useMutation({ mutationFn: () => api.papersClearMyPin(vid), onSuccess: () => { qc.invalidateQueries({ queryKey: ['papers-pin', vid] }); addToast({ type: 'success', title: 'Scout PIN removed' }); } });
  // Shown on every vessel, a one-person boat's too: without a PIN a Scout shows what crew
  // see, which leaves out the owner's own papers and anything kept to officers (Jason,
  // 15 Oct 2026: the Scout offers My papers, so the PIN must be settable here).
  return (
    <section className="bg-slate-800/60 border border-slate-700 rounded-2xl p-4">
      <div className="flex items-start gap-3">
        <DevicePhoneMobileIcon className="w-6 h-6 text-amber-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold text-white">Your PIN on the Scouts</h3>
          <p className="text-sm text-slate-400">The Scouts aboard show the papers everyone can see. Enter your PIN on a Scout to see what you're entitled to — including your own papers — for ten minutes.</p>
          {!editing ? (
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <span className="text-sm text-slate-300">{data?.has_pin ? 'PIN set' : 'No PIN yet'}</span>
              <button type="button" onClick={() => setEditing(true)} className={btnPrimary}>{data?.has_pin ? 'Change PIN' : 'Set a PIN'}</button>
              {data?.has_pin && <button type="button" onClick={() => clear.mutate()} className={btnGhost}>Remove</button>}
            </div>
          ) : (
            <form className="mt-2 flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); if (pin !== again) { setError('The two PINs don’t match.'); return; } save.mutate(); }}>
              <label className="text-sm font-semibold text-slate-200">PIN (6 digits)<input inputMode="numeric" autoComplete="off" pattern={SCOUT_PIN_PATTERN} minLength={SCOUT_PIN_LENGTH} maxLength={SCOUT_PIN_LENGTH} className={`${input} mt-1 w-32`} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} autoFocus /></label>
              <label className="text-sm font-semibold text-slate-200">Again<input inputMode="numeric" autoComplete="off" maxLength={SCOUT_PIN_LENGTH} className={`${input} mt-1 w-32`} value={again} onChange={(e) => setAgain(e.target.value.replace(/\D/g, ''))} /></label>
              <button type="submit" disabled={save.isPending || pin.length < SCOUT_PIN_LENGTH} className={btnPrimary}>Save</button>
              <button type="button" onClick={() => { setEditing(false); setError(null); }} className={btnGhost}>Cancel</button>
              {error && <p role="alert" className="text-sm text-red-300 basis-full">{error}</p>}
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
